import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Lock, Users } from 'lucide-react';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { cn } from 'lib/utils';
import { VisibilityBadge } from 'components/blocks/visibility-field';
import type { SkillVisibility } from './skills.types';

/**
 * Skills-only visibility control (private/shared/public). Deliberately not
 * built on the shared `components/blocks/visibility-field` — that component's
 * `ResourceVisibility` type only has private/public and is used by 27 other
 * files (tools, knowledge base, agents, prompts); adding `shared` there would
 * add a third radio to every one of those unrelated features.
 */

const SKILL_VISIBILITY_META: Record<
  SkillVisibility,
  { icon: typeof Lock; titleKey: string; titleFallback: string }
> = {
  private: { icon: Lock, titleKey: 'PRIVATE', titleFallback: 'Private' },
  shared: { icon: Users, titleKey: 'SKILL_VISIBILITY_SHARED', titleFallback: 'Shared' },
  public: { icon: Globe, titleKey: 'PUBLIC', titleFallback: 'Public' },
};

const SKILL_VISIBILITY_OPTIONS: SkillVisibility[] = ['private', 'shared', 'public'];

export function SkillVisibilityField({
  value,
  onValueChange,
  disabled = false,
  className,
}: {
  value: SkillVisibility;
  onValueChange: (value: SkillVisibility) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const prefix = `skill-visibility-${useId()}`;
  const label = t('VISIBILITY') || 'Visibility';

  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      <Label className="text-sm font-medium leading-[1.6] text-grey-700 dark:text-true-white">
        {label}
      </Label>
      <RadioGroup
        value={value}
        onValueChange={(next) => onValueChange(next as SkillVisibility)}
        disabled={disabled}
        className="mt-1 flex flex-col gap-3 sm:flex-row sm:gap-6"
        aria-label={label}
      >
        {SKILL_VISIBILITY_OPTIONS.map((option) => {
          const meta = SKILL_VISIBILITY_META[option];
          const optionId = `${prefix}-${option}`;
          const Icon = meta.icon;

          return (
            <div key={option} className="flex items-start gap-2">
              <RadioGroupItem value={option} id={optionId} className="mt-0.5" />
              <Label htmlFor={optionId} className="flex cursor-pointer items-center gap-1.5 font-normal">
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="text-sm font-medium leading-[1.4] text-grey-900 dark:text-foreground">
                  {t(meta.titleKey) || meta.titleFallback}
                </span>
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}

export function SkillVisibilityBadge({
  value,
  className,
}: {
  value?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();

  if (value !== 'shared') {
    return <VisibilityBadge value={value} className={className} />;
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
        'border-[#fde68a] bg-[#fffbeb] text-[#92400e] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300',
        className
      )}
    >
      {t('SKILL_VISIBILITY_SHARED') || 'Shared'}
    </span>
  );
}
